import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '../../../organisms/Grid/Grid';
import AdaptableField from '../../../molecules/Adaptable/AdaptableField';
import styles from './ContributeFormNodeField.css';
import deepValueObject from '../../../../helpers/deepValueObject';
import { GPSCOORDINATES, NUMBER } from '../../../../constants/typology/fieldType';
import { DATA_TYPE_LIST } from '../../../../constants/typology/dataType';
import { get } from 'object-path';
import arePropsEqual from '../../../../helpers/arePropsEqual';

const ContributeFormNodeField = memo(({
  node,
  boxOverride,
  parentFieldName = '',
  readonly,
  answersValues = null,
  reviewTree,
  isNestedInDuplicable = false,
  isReview,
}) => {
  // Take boxOverrides into account when computing the final box
  const realBox = useMemo(
    () => ({
      ...node.box,
      y: boxOverride ? boxOverride.y : node.box.y,
    }),
    [node.box, boxOverride && boxOverride.y],
  );

  const name = `${parentFieldName}${node.id}`;

  const reviewData = useMemo(() => get(reviewTree, name.replace(/\[([\d]+)\]\./g, '.$1.')), [
    reviewTree,
    name,
  ]);

  const answersChoices = useMemo(() => {
    if (reviewData?.answers) {
      return reviewData.answers.map((choice) => ({
        value: choice,
        label: choice ? choice.toString() : '',
      }));
    }
    return (
      answersValues &&
      answersValues.reduce((result, initialValues) => {
        const value = deepValueObject(initialValues, name);
        let label = value;

        if (node.type === GPSCOORDINATES && value) {
          label = Object.values(value).join(', ');
        }

        return value
          ? [...result, { value, label: Array.isArray(label) ? label.join(', ') : label }]
          : [];
      }, [])
    );
  }, [answersValues]);

  return (
    <Grid.Box
      {...realBox}
      isNestedInDuplicable={isNestedInDuplicable}
      className={styles.ContributeFormNodeField}
    >
      <AdaptableField
        isFocusOnInit={node?.isFocusOnInit}
        name={DATA_TYPE_LIST.includes(node.type) ? node.id : name}
        fieldType={node.type}
        label={node.label}
        validation={node}
        verticalAlign={true}
        isBignumber={node.type === NUMBER ? true : undefined}
        readonly={readonly}
        /*
          If the field is inside a duplicable block, we don't want to display incoherent
          answers choices (each field would have only one possible answer according to
          it's position in the rows...)
        */
        answersChoices={isNestedInDuplicable ? null : answersChoices}
        /*
          If we are in Review && the field is inside a duplicable block, we don't want to
          display the confidence because it would always be considered 40 (=green / =OK)
          (whereas if we are outside of a duplicable block OR if we are not in review
          and "color" is forced on the hydrated field, we want to show it).
        */
        confidence={isReview && isNestedInDuplicable ? null : reviewData?.confidence}
      />
    </Grid.Box>
  );
}, arePropsEqual);

ContributeFormNodeField.displayName = 'ContributeFormNodeField';

ContributeFormNodeField.propTypes = {
  answersValues: PropTypes.array,
  node: PropTypes.object,
  isNestedInDuplicable: PropTypes.bool,
  isReview: PropTypes.bool,
  boxOverride: PropTypes.object,
  parentFieldName: PropTypes.string,
  readonly: PropTypes.bool,
  reviewTree: PropTypes.object,
};

export default ContributeFormNodeField;
